/* global.js — progressive enhancement custom elements. Vanilla, no globals. */

/**
 * <header-nav> — mobile off-canvas menu (toggle, overlay, close button,
 * link-close, scroll lock) plus the "Shop" mega menu (hover w/ close delay
 * on desktop, tap-accordion on mobile — same markup, CSS + this class just
 * switch how it opens). Enhances the header markup rendered by
 * sections/header.liquid. Works without JS (nav is visible on desktop).
 */
class HeaderNav extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-header-toggle]');
    this.close = this.querySelector('[data-header-close]');
    this.overlay = this.querySelector('[data-header-overlay]');
    this.nav = this.querySelector('[data-header-nav]');
    if (!this.toggle || !this.nav) return;

    this.onToggleClick = this.onToggleClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.toggle.addEventListener('click', this.onToggleClick);
    if (this.close) this.close.addEventListener('click', this.onCloseClick);
    if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
    this.nav.addEventListener('click', this.onNavClick);
    this.addEventListener('keydown', this.onKeydown);

    this.initMegaMenu();
  }

  disconnectedCallback() {
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
    if (this.close) this.close.removeEventListener('click', this.onCloseClick);
    if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
    if (this.nav) this.nav.removeEventListener('click', this.onNavClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  onToggleClick() {
    const open = this.getAttribute('data-open') === 'true';
    this.setOpen(!open);
  }

  onCloseClick() {
    this.setOpen(false);
  }

  onOverlayClick() {
    this.setOpen(false);
  }

  onNavClick(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (link) this.setOpen(false);
  }

  setOpen(open) {
    this.setAttribute('data-open', String(open));
    this.toggle.setAttribute('aria-expanded', String(open));
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) {
      const firstLink = this.nav.querySelector('a, button');
      if (firstLink) firstLink.focus();
    } else {
      const megaTrigger = this.querySelector('[data-mega-trigger]');
      if (megaTrigger) megaTrigger.setAttribute('aria-expanded', 'false');
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.getAttribute('data-open') === 'true') {
      this.setOpen(false);
      this.toggle.focus();
    }
  }

  /**
   * "Shop" mega menu: a single <button data-mega-trigger> + sibling
   * <div data-mega-panel>. Click always toggles (this is what makes it work
   * as a mobile accordion). On top of that, desktop pointer users get
   * hover-to-open with a ~200ms close delay so the cursor can travel from
   * the nav link down into the dropdown without it flickering shut.
   */
  initMegaMenu() {
    const item = this.querySelector('[data-mega-item]');
    const trigger = this.querySelector('[data-mega-trigger]');
    if (!item || !trigger) return;

    let closeTimer = null;
    const cancelClose = () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    };
    const openMega = () => {
      cancelClose();
      trigger.setAttribute('aria-expanded', 'true');
    };
    const closeMegaNow = () => trigger.setAttribute('aria-expanded', 'false');
    const scheduleClose = () => {
      cancelClose();
      closeTimer = setTimeout(closeMegaNow, 200);
    };

    // Hover/focus auto-open is a desktop-mouse enhancement layered on top of
    // click-to-toggle. A touch tap fires a synthetic mouseenter + focus
    // before its click, so binding these unconditionally opens the panel via
    // mouseenter/focusin and then the click handler's toggle immediately
    // closes it again — the panel never stays visible on touch. Restrict
    // hover/focus auto-open to devices whose primary input can actually hover.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      item.addEventListener('mouseenter', openMega);
      item.addEventListener('mouseleave', scheduleClose);
      item.addEventListener('focusin', openMega);
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) closeMegaNow();
      });
    }

    trigger.addEventListener('click', () => {
      cancelClose();
      const open = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!open));
    });
  }
}

if (!customElements.get('header-nav')) customElements.define('header-nav', HeaderNav);
/* <cart-drawer> is defined in assets/cart.js, which owns all cart AJAX logic. */

/* --- Quantity inputs: auto-submit the closest cart form on change --------- */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLElement) || !input.matches('[data-quantity-input]')) return;
  const form = input.closest('form');
  if (form && typeof form.requestSubmit === 'function') {
    form.requestSubmit();
  }
});

/* --- Quantity steppers: +/- buttons adjust the sibling number input ------- */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target && target.closest('[data-quantity-decrease], [data-quantity-increase]');
  if (!button) return;
  const wrapper = button.closest('[data-quantity-wrapper]');
  const input = wrapper && wrapper.querySelector('input[type="number"]');
  if (!input) return;

  const min = Number(input.min) || 1;
  const step = Number(input.step) || 1;
  const current = Number(input.value) || min;
  const next = button.matches('[data-quantity-decrease]') ? Math.max(min, current - step) : current + step;

  input.value = next;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* --- Product gallery: thumbnail click swaps the active main image; on
   mobile the main track is a native CSS scroll-snap strip, so an
   IntersectionObserver keeps the thumbnails/dots in sync with swipes. ----- */
(function initProductGalleries() {
  const galleries = document.querySelectorAll('[data-product-gallery]');
  if (!galleries.length) return;

  galleries.forEach((gallery) => {
    const track = gallery.querySelector('[data-gallery-main-track]');
    const mainItems = gallery.querySelectorAll('[data-gallery-main-item]');
    const thumbnails = gallery.querySelectorAll('[data-gallery-thumbnail]');
    const dots = gallery.querySelectorAll('[data-gallery-dot]');

    function setActive(mediaId) {
      mainItems.forEach((item) => item.classList.toggle('is-active', item.dataset.mediaId === mediaId));
      thumbnails.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId));
      dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.mediaId === mediaId));
    }

    thumbnails.forEach((thumb) => {
      thumb.addEventListener('click', () => setActive(thumb.dataset.mediaId));
    });

    if (track && mainItems.length > 1 && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActive(entry.target.dataset.mediaId);
          });
        },
        { root: track, threshold: 0.6 }
      );
      mainItems.forEach((item) => observer.observe(item));
    }
  });
})();

/* --- Reveal on scroll ----------------------------------------------------- */
(function initReveal() {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const elements = document.querySelectorAll('.reveal');
  if (!elements.length) return;

  if (reduceMotion || !('IntersectionObserver' in window)) {
    elements.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        obs.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  elements.forEach((el) => observer.observe(el));
})();
